/**
 * DI-3-F — the upload session's SAS, as the browser uses it (story §2.1
 * `sas.ts`; read against the gateway's `upload-sas.ts` + `mintPrefixWriteSas`).
 *
 * `openUploadSession` answers ONE target: `{prefix, url, expireDateMs}`. The
 * `url` is `https://<account>/aiq-<tenant>/data-acquisitions/<da>/raw/?<sas>`
 * — the gateway signs the CONTAINER (`sr=c`, permissions `cw` = create +
 * write, 6 h) and splices the prefix into the path. So:
 *
 *   * a blob's URL = the target's PATH + the encoded file name + the SAME
 *     query string (`blobUrl`). The file name is the round trip's wire name,
 *     so the blob name equals `images.image_filename` (`~n` twins included);
 *   * `cw` has no read: no HEAD, no list. Whether a blob is there is known
 *     from the ledger (`ledger.ts`) and from the ingest's verification, never
 *     from the storage;
 *   * the prefix is advisory (DI-1-6 measured it) — "refuses a PUT outside
 *     the prefix" is not a property to rely on or to assert.
 *
 * `SasSource` opens a session once per set per upload run and re-opens it
 * when its expiry is less than 10 min away (`SAS_REFRESH_MARGIN_MS`) or a PUT
 * answered 403 (`invalidate()`): the 6 h TTL can run out on a slow uplink.
 */
import type { UploadSession } from "../api/uploadSessions.js";

/** Re-open the session when its SAS expires within this margin. */
export const SAS_REFRESH_MARGIN_MS = 10 * 60 * 1000;

/** The gateway's `UPLOAD_BLOCK_SIZE` — used when a session names none. */
export const DEFAULT_BLOCK_SIZE = 1024 * 1024;

/** `…/raw/?sv=…` + `DJI_0001.JPG` → `…/raw/DJI_0001.JPG?sv=…`. */
export function blobUrl(sasUrl: string, filename: string): string {
  const u = new URL(sasUrl);
  const path = u.pathname.endsWith("/") ? u.pathname : `${u.pathname}/`;
  u.pathname = `${path}${encodeURIComponent(filename)}`;
  return u.toString();
}

/**
 * DI-3-S75 §3.1 — a `*_pos.txt`'s URL: the DA's `aux/` area, a sibling of
 * the session's `raw/` prefix in the same container (the SAS is
 * container-scoped `cw`, the prefix advisory — see above):
 * `…/data-acquisitions/<da>/raw/?sv=…` + `NX84188` + `DJI_…_pos.txt` →
 * `…/data-acquisitions/<da>/aux/NX84188/DJI_…_pos.txt?sv=…`. Every folder
 * segment is encoded on its own; an empty folder (a file at the top level)
 * puts the file directly under `aux/`.
 */
export function auxBlobUrl(
  sasUrl: string,
  relativeFolder: string,
  filename: string,
): string {
  const u = new URL(sasUrl);
  const path = u.pathname.endsWith("/") ? u.pathname : `${u.pathname}/`;
  const m = /^(.*\/)raw\/$/.exec(path);
  if (!m) throw new Error(`the upload target is no raw/ prefix: ${path}`);
  const folder = relativeFolder
    .split("/")
    .filter((s) => s !== "")
    .map((s) => `${encodeURIComponent(s)}/`)
    .join("");
  u.pathname = `${m[1] ?? ""}aux/${folder}${encodeURIComponent(filename)}`;
  return u.toString();
}

/** Is the session's SAS still good for at least the refresh margin? */
export function sasFresh(expireDateMs: number, nowMs: number): boolean {
  return expireDateMs - nowMs > SAS_REFRESH_MARGIN_MS;
}

export interface OpenedSession {
  sessionId: string;
  url: string;
  expireDateMs: number;
  blockSize: number;
}

/** The one prefix target of a session (the gateway issues exactly one). */
export function sessionTarget(s: UploadSession): OpenedSession {
  const t = s.sasUrls.find((x) => typeof x.url === "string" && x.url !== "");
  if (!t) throw new Error("the upload session carries no SAS target");
  return {
    sessionId: s.sessionId,
    url: t.url,
    expireDateMs: t.expireDateMs,
    blockSize: s.blockSize > 0 ? s.blockSize : DEFAULT_BLOCK_SIZE,
  };
}

export class SasSource {
  private current: OpenedSession | null = null;
  private opening: Promise<OpenedSession> | null = null;

  constructor(
    private readonly open: () => Promise<UploadSession>,
    private readonly now: () => number = () => Date.now(),
    /** Told about every (re-)opened session — the runner persists its id. */
    private readonly onOpened: (s: OpenedSession) => void = () => undefined,
  ) {}

  /** The live session, opened or re-opened when needed. */
  async get(): Promise<OpenedSession> {
    const c = this.current;
    if (c && sasFresh(c.expireDateMs, this.now())) return c;
    this.opening ??= this.open()
      .then((s) => {
        const t = sessionTarget(s);
        this.current = t;
        this.onOpened(t);
        return t;
      })
      .finally(() => {
        this.opening = null;
      });
    return this.opening;
  }

  /** A PUT answered 403: the next `get()` opens a new session. */
  invalidate(url?: string): void {
    if (!url || this.current?.url === url) this.current = null;
  }

  /** The session opened last (its id is where the manifest is completed). */
  get session(): OpenedSession | null {
    return this.current;
  }
}
